import { Bars3Icon } from '@heroicons/react/24/outline';
import HomeLink from 'components/HomeLink';
import { Link } from 'gatsby';
import React, { PropsWithChildren, useState } from 'react';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import styled from 'styled-components';

export interface NavbarProps {
    /**
     * The path for this current page
     *
     * @see LayoutProps.path
     */
    path?: string;
}

const CustomNavbar: React.FC<NavbarProps> = ({ path }) => {
    // Normalize the path for comparison by removing any trailing slash
    const currentPath = path?.replace(/\/$/, '');

    const [expanded, setExpanded] = useState(false);

    interface NavLinkProps {
        /* The path to the current page, without any trailing slashes */
        currentPath?: string;
        to: string;
    }

    const CustomNavLink: React.FC<PropsWithChildren<NavLinkProps>> = ({
        currentPath,
        to,
        children,
    }) => {
        const isCurrentPage = currentPath?.startsWith(to);
        return isCurrentPage ? (
            <Link
                onClick={() =>
                    setTimeout(() => {
                        setExpanded(false);
                    }, 150)
                }
                to={to}
                aria-current="page"
                className="nav-link navbar-link-active">
                {children}
            </Link>
        ) : (
            <Link
                onClick={() =>
                    setTimeout(() => {
                        setExpanded(false);
                    }, 150)
                }
                to={to}
                className="nav-link navbar-link">
                {children}
            </Link>
        );
    };

    const DownloadLink: React.FC = () => {
        return (
            <Link
                onClick={() =>
                    setTimeout(() => {
                        setExpanded(false);
                    }, 150)
                }
                to="/download"
                aria-current="page"
                className="nav-link navbar-download-link">
                Download
            </Link>
        );
    };

    const toggleHandler = (expanded: boolean) => {
        const navbar =
            document.querySelector<HTMLDivElement>('.navbar-collapse');
        if (navbar) {
            navbar.style.transition =
                'min-height .25s ease-out, height .25s ease-out';
            navbar.style.minHeight = '0vh';
            if (expanded) {
                navbar.style.borderTop = '1px solid #ccc';
                navbar.style.marginTop = '10px';
                navbar.style.paddingTop = '10px';
                navbar.style.minHeight = '100vh';
            } else {
                navbar.style.borderTop = 'none';
                navbar.style.marginTop = '0px';
                navbar.style.paddingTop = '0px';
                navbar.style.minHeight = '0vh';
            }
        }
    };

    return (
        <Navbar
            fixed="top"
            expand="lg"
            expanded={expanded}
            className="px-4 pt-2 pb-2 pb-md-3 pt-md-3 px-md-5"
            onToggle={toggleHandler}>
            <Navbar.Brand style={leadingTrailingStyle}>
                <HomeLink />
            </Navbar.Brand>
            <Navbar.Toggle
                aria-controls="navbar-main-nav"
                className="my-2"
                onClick={() => setExpanded(!expanded)}>
                <Bars3Icon width={24} className="nav-toggle" />
            </Navbar.Toggle>
            <Navbar.Collapse id="navbar-main-nav" dimension="height">
                <Nav className="mx-auto gap-1 gap-md-4">
                    <CustomNavLink {...{ currentPath }} to="/#features">
                        Features
                    </CustomNavLink>
                    <CustomNavLink {...{ currentPath }} to="/#pricing">
                        Pricing
                    </CustomNavLink>
                    <CustomNavLink {...{ currentPath }} to="/about">
                        About
                    </CustomNavLink>
                    <CustomNavLink {...{ currentPath }} to="/blog">
                        Blog
                    </CustomNavLink>
                    <CustomNavLink {...{ currentPath }} to="/community">
                        Community
                    </CustomNavLink>
                </Nav>
                <Nav>
                    <div
                        className="pt-4 pb-4 pt-lg-0 pb-lg-0 d-md-block"
                        style={leadingTrailingStyle}>
                        <DownloadButton to="/download">Download</DownloadButton>
                        <LoginButton href="https://web.ente.io">
                            Login
                        </LoginButton>
                    </div>
                </Nav>
            </Navbar.Collapse>
        </Navbar>
    );
};

export default CustomNavbar;

// To make the middle Nav.Links appear visually centered, give both the
// Navbar.Brand and the trailing "Login" Nav.Link the same minimum width.
//
// 100px is an arbitrary width large enough to fit the both the leading and
// trailing content.

const leadingTrailingStyle = {
    minWidth: '207px',
    margin: '0',
};

// This is intentionally not an ExternalLink – we don't want a new tab to open
// when the user tries to login from the header of the website (a new tab
// opening for web.ente.io links from other places is fine, we only need to
// special case the navbar currently).

const LoginButton = styled.a.attrs({
    className: 'mt-2 mt-md-0',
})`
    text-decoration: none;
    border-radius: 12px;
    padding: 12px 24px 12px 24px;
    border: 1px solid #01b72f;
    font-weight: 700;
    font-family: Gilroy;
`;

const DownloadButton = styled(Link).attrs({
    className: 'mt-2 mt-md-0',
})`
    text-decoration: none;
    border-radius: 12px;
    background-color: #01b72f;
    padding: 12px 24px 12px 24px;
    color: #ffffff;
    margin-right: 24px;
    font-weight: 700;
    font-family: Gilroy;
    &:hover {
        color: #ffffff;
    }
`;
